import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client.js';

export type AbschlussStatus = 'offen' | 'in_prep' | 'abgenommen';

export interface ChecklisteItem {
  id: string;
  item: string;
  done: boolean;
  signed_by?: string;
}

export interface ProjektabschlussData {
  lessons_learned_positiv: string;
  lessons_learned_negativ: string;
  abnahme_checkliste: ChecklisteItem[];
  uebergabe_doku: string;
  abschluss_status: AbschlussStatus;
  abschluss_datum: string | null; // ISO yyyy-mm-dd
  abnahme_unterzeichner: string;
}

const EMPTY: ProjektabschlussData = {
  lessons_learned_positiv: '',
  lessons_learned_negativ: '',
  abnahme_checkliste: [],
  uebergabe_doku: '',
  abschluss_status: 'offen',
  abschluss_datum: null,
  abnahme_unterzeichner: '',
};

export interface UseProjektabschlussResult {
  data: ProjektabschlussData;
  isLoading: boolean;
  save: (next: ProjektabschlussData) => Promise<void>;
  isSaving: boolean;
}

const KEY = (pid: string) => ['projects', pid, 'abschluss'] as const;

export function useProjektabschluss(projectId: string | undefined): UseProjektabschlussResult {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEY(projectId ?? ''),
    queryFn: () => apiClient.get<ProjektabschlussData>(`/api/v1/projects/${projectId}/abschluss`),
    enabled: !!projectId,
  });

  const mutation = useMutation({
    mutationFn: (next: ProjektabschlussData) =>
      apiClient.patch<ProjektabschlussData>(`/api/v1/projects/${projectId}/abschluss`, next),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(projectId ?? '') }),
  });

  return {
    data: query.data?.data ?? EMPTY,
    isLoading: query.isLoading,
    save: async (next: ProjektabschlussData) => {
      await mutation.mutateAsync(next);
    },
    isSaving: mutation.isPending,
  };
}
